// The bank's members (RFC-056 §1, D6): who has a place in the bank and as
// what, as the relay keeps it. A bank admin gives a person the role user
// until a day, or for good, and ends it, asked first; an admin's place is
// Share's Admin page's, so an admin's row has nothing to end.
import { useCallback, useEffect, useState } from "react";
import { isAddress } from "../address";
import type { BankClient, BankMember } from "../bank";
import { t, whenDay } from "../i18n";
import { useConfirm } from "./Confirm";
import { FormDialog } from "./FormDialog";
import { RowMenu, type MenuItem } from "./RowMenu";
import { EmptyRows, Grouped, initialOf } from "./Rows";
import { endOfDay } from "./Admins";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

function MemberRow({ m, onEnd }: { m: BankMember; onEnd: (m: BankMember) => void }) {
  const sub = [
    m.name,
    m.share_admin && t("bank:membersShareAdmin"),
    m.granted_by && t("bank:membersBy", { email: m.granted_by }),
    m.expires_at && t("bank:membersUntil", { w: whenDay(m.expires_at) }),
  ].filter(Boolean);
  const menu: MenuItem[] = m.role === "user" && m.live ? [{ label: t("bank:membersEnd"), onSelect: () => onEnd(m), danger: true }] : [];
  return (
    <li className={`share-row ${m.live ? "is-live" : "is-ended"}`} data-testid="bank-member">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(m.email)}
      </span>
      <div className="row-main">
        <span className="row-title">{m.email}</span>
        <div className="row-sub">{sub.join(" · ")}</div>
      </div>
      <div className="row-meta">
        <span className="chip">{m.role === "admin" ? t("bank:roleAdmin") : t("bank:roleUser")}</span>
      </div>
      <RowMenu label={t("shares:moreFor", { title: m.email })} items={menu} />
    </li>
  );
}

function AddDialog({ onSubmit, onClose }: { onSubmit: (email: string, until: string) => Promise<void>; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [until, setUntil] = useState("");
  return (
    <FormDialog
      id="bank-member-dialog"
      testid="bank-add-member"
      title={t("bank:membersAddTitle")}
      submitLabel={t("bank:membersAdd")}
      canSubmit={isAddress(email)}
      onSubmit={() => onSubmit(email.trim(), until)}
      onClose={onClose}
    >
      <label className="field">
        {t("bank:membersEmail")}
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label={t("bank:membersEmail")} />
      </label>
      <label className="field">
        {t("bank:membersUntilLabel")}
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} aria-label={t("bank:membersUntilLabel")} />
      </label>
      <p className="muted small flush">{t("bank:membersAddNote")}</p>
    </FormDialog>
  );
}

export function BankMembers({ client }: { client: BankClient }) {
  const [list, setList] = useState<BankMember[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [ask, confirmDialog] = useConfirm();
  const reload = useCallback(async () => {
    try {
      setList(await client.members());
    } catch (err) {
      setError(errText(err));
    }
  }, [client]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const add = async (email: string, until: string) => {
    const m = await client.grantMember(email, endOfDay(until));
    setDone(t("bank:membersAdded", { email: m.email }));
    setError(null);
    setAdding(false);
    await reload();
  };
  const end = async (m: BankMember) => {
    if (!(await ask(t("bank:membersEndAsk", { email: m.email }), { action: t("bank:membersEnd"), danger: true }))) return;
    try {
      await client.endMember(m.user_id);
      setDone(t("bank:membersEnded", { email: m.email }));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
    await reload();
  };
  const all = list ?? [];
  return (
    <div data-testid="bank-members">
      <div className="panel-head">
        <p className="muted small panel-lead">{t("bank:membersLead")}</p>
        <button className="primary" aria-haspopup="dialog" aria-expanded={adding} aria-controls={adding ? "bank-member-dialog" : undefined} onClick={() => (setDone(null), setAdding(true))}>
          {t("bank:membersAdd")}
        </button>
      </div>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {done && (
        <p className="ok-text" role="status">
          {done}
        </p>
      )}
      {list === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : (
        <Grouped
          id="bank-members"
          groups={[
            { title: t("bank:roleAdmin"), items: all.filter((m) => m.role === "admin" && m.live), none: undefined },
            { title: t("bank:roleUser"), items: all.filter((m) => m.role === "user" && m.live), none: <EmptyRows text={t("bank:membersNoUsers")} /> },
            { title: t("bank:membersEndedHead"), items: all.filter((m) => !m.live) },
          ]}
          row={(m) => <MemberRow key={m.user_id || m.email} m={m} onEnd={(x) => void end(x)} />}
        />
      )}
      {adding && <AddDialog onSubmit={add} onClose={() => setAdding(false)} />}
      {confirmDialog}
    </div>
  );
}
